import { useRef, useEffect } from 'react';
import { LoginForm } from 'session-manager/views/login-form/LoginForm';
import { SessionManagerService } from 'session-manager/services/SessionManagerService';

import { Button, IconButton, LinkButton } from '@client:common/button';
import { Ripple } from '@client:common/effects/ripple';
import { Icon } from '@client:common/icon';
import { Dialog } from '@client:common/overlay/dialog';
import { GlobalStore } from '@client:common/global-store';
import { FormControl, ShortTextFormField } from '@client:common/form';
import { ProgressIndicator } from '@client:common/overlay/progress-indicator';
import { Notification } from '@client:common/overlay/notification';

import { MarkerPage, Page } from '../../models/FlowChart';

import './WizardControl.light.scss';
import './WizardControl.dark.scss';

interface Props {
  nextPage?: Page;
  currentPage: Page;
  previousPage?: Page;
  onGoToPage: (page: Page) => void;
  onStartOver: () => void;
  onSaveSession: (name: string) => void;
  onComplete: () => void;
  isExisting: boolean;
  name: string;
  isMobile: boolean;
}

export function WizardControl(props: Props) {
  const {
    previousPage,
    currentPage,
    nextPage,
    onGoToPage,
    onStartOver,
    onSaveSession,
    onComplete,
    isExisting,
    name,
    isMobile
  } = props;

  const globalStore = GlobalStore.getInstance();
  const authSubscription: any = globalStore.select("authenticatedAccount");
  const isAuthenticated = authSubscription.source._value;
  const sessionName = useRef(new FormControl(""));

  const measuringRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updatePosition = () => {
      if (isMobile) {
        const override = {
          right: '1rem',
          bottom: '5.75rem',
        };
        GlobalStore.getInstance().update({ assistantPositionOverride: override });
      } else {
        GlobalStore.getInstance().update({ assistantPositionOverride: null });
      }
    };

    updatePosition();

    const resizeObserver = new ResizeObserver(updatePosition);
    if (measuringRef.current) {
      resizeObserver.observe(measuringRef.current);
    }

    return () => {
      resizeObserver.disconnect();
      GlobalStore.getInstance().update({ assistantPositionOverride: null });
    };
  }, [isMobile]);

  const displaySessionDialog = async () => {
    const dialogRef = Dialog.create(
      <>
        <IconButton
          className="session-dialog__close"
          style="basic"
          color="primary"
          onClick={() => {
            dialogRef.close();
          }}
        >
          <Icon icon="close" />
          <Ripple
            fixed
            duration={1000} />
        </IconButton>
        <>
          <ShortTextFormField
            label="Session name (optional)"
            formControl={sessionName.current}
          />
          <div className="confirm-name-container">
            <Button
              style="raised"
              color="primary"
              onClick={() => {
                dialogRef.close();
                onSaveSession(sessionName.current.getValue());
              }}
            >
              DONE
            </Button>
          </div>
        </>
      </>
    )
      .addClassName("session-name-dialog")
      .open();
  };

  const onShowLoginForm = () => {
    const dialogRef = Dialog.create(
      <>
        <IconButton
          className="login-dialog__close"
          style="basic"
          color="primary"
          onClick={() => {
            dialogRef.close();
          }}
        >
          <Ripple
            fixed
            duration={1000} />
          <Icon icon="close" />
        </IconButton>
        <LoginForm
          onLogin={(email, password) => {
            const sessionManagerService = SessionManagerService.getInstance();

            ProgressIndicator.show();
            sessionManagerService
              .signIn(email, password)
              .then((authenticatedAccount) => {
                globalStore.update({
                  authenticatedAccount
                });

                dialogRef.close();
                displaySessionDialog();
              })
              .catch(() => {
                Notification.open(
                  "Email or password is incorrect, please try again"
                );
              })
              .finally(() => {
                ProgressIndicator.hide();
              });
          }}
        />
      </>
    )
      .addClassName("login-dialog")
      .open();
  };

  const onSaveProgress = () => {
    if (isAuthenticated) {
      /* Display session dialog for creating new session, otherwise update session with existing session name */
      if (!isExisting) {
        displaySessionDialog();
      } else {
        onSaveSession(name);
      }
    } else {
      onShowLoginForm();
    }
  };

  return (
    <div className="wizard-control">
      <LinkButton
        className="wizard-control__action go-to-home-page"
        to="/">
        <Icon icon="home" />
        <span className="wizard-control__action-text">Home</span>
      </LinkButton>

      <IconButton
        className="wizard-control__action save-progress"
        style="basic"
        color="primary"
        onClick={onSaveProgress}
      >
        <Icon icon="save" />
        <span className="wizard-control__action-text">Save Progress</span>
      </IconButton>

      <div ref={measuringRef} className="wizard-control__spacer" />

      {previousPage && (
        <IconButton
          className="wizard-control__action go-to-previous-page"
          style="stroked"
          color="primary"
          onClick={() => onGoToPage(previousPage)}
        >
          <Ripple />
          <Icon icon="chevron_left" />
          <span className="wizard-control__action-text">
            {previousPage.label}
          </span>
        </IconButton>
      )}
      {currentPage === MarkerPage.EXIT ? (
        <IconButton
          className="wizard-control__action start-over"
          style="raised"
          color="primary"
          onClick={onStartOver}
        >
          <Ripple />
          <Icon icon="replay" />
          <span className="wizard-control__action-text">Start over</span>
        </IconButton>
      ) : nextPage !== MarkerPage.EXIT ? (
        <IconButton
          className="wizard-control__action go-to-next-page"
          style="raised"
          color="primary"
          disabled={!nextPage}
          onClick={() => onGoToPage(nextPage)}
        >
          <Ripple />
          <span className="wizard-control__action-text">Next</span>
          <Icon icon="chevron_right" />
        </IconButton>
      ) : (
        <IconButton
          className="wizard-control__action show-recommendations"
          style="raised"
          color="primary"
          onClick={() => {
            onComplete();
            onGoToPage(nextPage);
          }}
        >
          <Ripple />
          <Icon icon="check" />
          <span className="wizard-control__action-text">
            Show recommendations
          </span>
        </IconButton>
      )}
    </div>
  );
}
